import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import type { Node as ProseMirrorNode } from '@milkdown/kit/prose/model';
import { $node, $remark } from '@milkdown/kit/utils';
import type { Link, Paragraph, Root, Text } from 'mdast';
import { visit } from 'unist-util-visit';

export const VIDEO_NODE_NAME = 'video';
export const DEFAULT_VIDEO_WIDTH = 640;
export const DEFAULT_VIDEO_HEIGHT = 360;

/**
 * Video file extensions supported for detection
 */
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.wmv', '.flv', '.webm', '.mkv', '.m4v'];

/**
 * Check if a URL points to a video file by extension
 */
export function isVideoFile(url: string): boolean {
  const urlWithoutParams = url.split('?')[0].split('#')[0].toLowerCase();

  return VIDEO_EXTENSIONS.some((ext) => urlWithoutParams.endsWith(ext));
}

/**
 * URLs that serve uploaded/binary content but do not end in a video extension
 * (mirrors platform shared/editor/rules/attachments.ts isAttachment heuristics).
 */
function isAttachmentLikeVideoUrl(url: string): boolean {
  const lower = url.toLowerCase();

  return lower.includes('/api/attachments.redirect') || lower.includes('/brainfish-s3/') || lower.includes('x-amz-signature');
}

/**
 * Attachment video links (platform markdown-it): last segment after spaces is treated as
 * size; video when `size?.includes("x")`, not only strict `WIDTHxHEIGHT` digits.
 */
function linkTextHasAttachmentVideoSizeToken(text: string): boolean {
  const parts = text.trim().split(/\s+/);
  const last = parts[parts.length - 1];

  return Boolean(last?.includes('x'));
}

/**
 * Extract filename from URL for use as default title
 */
export function extractFilenameFromUrl(url: string): string {
  let filename = '';
  try {
    filename = new URL(url).pathname.split('/').pop() || '';
  } catch {
    filename = url.split('?')[0].split('#')[0].split('/').pop() || '';
  }
  if (!filename) return 'Video';

  return filename.replace(/\.[^/.]+$/, '') || filename;
}

/**
 * Parse dimensions from link text (e.g., "My Video 640x360" -> { title: "My Video", width: 640, height: 360 })
 *
 * @param attachmentStyleLink - When true, a last token containing `x` (not strict WxH) is treated as a bogus
 *   size suffix (platform attachment format). When false (plain video file URLs), words like "remix" must not
 *   be stripped; use the legacy title path instead.
 */
function parseLinkText(text: string, attachmentStyleLink: boolean): { title: string; width: number; height: number } {
  const parts = text.trim().split(/\s+/);
  const lastPart = parts[parts.length - 1];
  const titleBeforeLast = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';

  if (lastPart && /^\d+x\d+$/.test(lastPart)) {
    const [width, height] = lastPart.split('x').map(Number);

    return { title: titleBeforeLast, width, height };
  }

  if (attachmentStyleLink && lastPart?.includes('x')) {
    return { title: titleBeforeLast, width: DEFAULT_VIDEO_WIDTH, height: DEFAULT_VIDEO_HEIGHT };
  }

  return { title: extractFilenameFromUrl(text), width: DEFAULT_VIDEO_WIDTH, height: DEFAULT_VIDEO_HEIGHT };
}

/**
 * Remark plugin that transforms video links into videoNode AST nodes.
 *
 * Detects:
 * - Links with video file extensions (.mp4, .webm, etc.)
 * - Markdown format: [title WxH](url.mp4) or [title](url.mp4)
 * - Internal attachment links: [title WxH](/api/attachments.redirect?...) (platform export format)
 */
/** Applies the same mdast transform as `remarkVideoPlugin` (exported for unit tests). */
export function transformVideoLinksInMdast(tree: Root): void {
  visit(tree, 'paragraph', (node: Paragraph, index, parent) => {
    if (index === undefined || !parent) return;

    if (node.children.length !== 1 || node.children[0].type !== 'link') return;

    const link = node.children[0] as Link;
    const href = link.url;

    const textContent = link.children
      .filter((child): child is Text => child.type === 'text')
      .map((child) => child.value)
      .join('');

    if (!isVideoFile(href) && !(isAttachmentLikeVideoUrl(href) && linkTextHasAttachmentVideoSizeToken(textContent))) {
      return;
    }

    const { title, width, height } = parseLinkText(textContent, isAttachmentLikeVideoUrl(href));
    const finalTitle = title || extractFilenameFromUrl(href);

    (parent.children as any)[index] = {
      type: 'videoNode',
      attributes: {
        src: href,
        title: finalTitle,
        width,
        height,
      },
    };
  });
}

export const remarkVideoPlugin = $remark('video-transform', () => {
  return () => (tree: Root) => {
    transformVideoLinksInMdast(tree);
  };
});

/**
 * Video node schema for ProseMirror
 *
 * Attrs MUST match Platform's Video.tsx for Y.js collab sync:
 * - id: unique identifier (can be null)
 * - src: video URL
 * - width: pixel width
 * - height: pixel height
 * - title: caption/alt text
 */
export const videoNode = $node(VIDEO_NODE_NAME, () => ({
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false, // Match Platform: draggable: false (Firefox bug)
  defining: true,
  marks: '',
  attrs: {
    id: { default: null },
    src: { default: null },
    width: { default: null },
    height: { default: null },
    title: { default: '' },
  },
  parseDOM: [
    {
      priority: 100,
      tag: 'video',
      getAttrs: (dom) => {
        const element = dom as HTMLVideoElement;

        return {
          id: element.id || null,
          src: element.getAttribute('src'),
          title: element.getAttribute('title') || '',
          width: parseInt(element.getAttribute('width') ?? '', 10) || null,
          height: parseInt(element.getAttribute('height') ?? '', 10) || null,
        };
      },
    },
    {
      // Also parse our wrapper div for NodeView compatibility
      tag: 'div.video-block',
      getAttrs: (dom) => {
        const element = dom as HTMLElement;

        return {
          id: element.getAttribute('data-id') || null,
          src: element.getAttribute('data-src'),
          title: element.getAttribute('data-title') || '',
          width: parseInt(element.getAttribute('data-width') ?? '', 10) || null,
          height: parseInt(element.getAttribute('data-height') ?? '', 10) || null,
        };
      },
    },
  ],
  toDOM: (node: ProseMirrorNode) => [
    'div',
    {
      class: 'video-block',
      'data-id': node.attrs.id,
      'data-src': node.attrs.src,
      'data-title': node.attrs.title,
      'data-width': node.attrs.width,
      'data-height': node.attrs.height,
      contenteditable: 'false',
    },
    [
      'video',
      {
        id: node.attrs.id,
        src: node.attrs.src,
        controls: 'true',
        width: node.attrs.width,
        height: node.attrs.height,
        title: node.attrs.title,
      },
    ],
  ],
  parseMarkdown: {
    match: (node) => node.type === 'videoNode',
    runner: (state, node, type) => {
      const attrs = node.attributes as {
        src: string;
        title: string;
        width: number;
        height: number;
      };
      state.addNode(type, {
        src: attrs.src,
        title: attrs.title,
        width: attrs.width,
        height: attrs.height,
      });
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === VIDEO_NODE_NAME,
    runner: (state, node) => {
      // Serialize to Platform's format: [title WxH](src)
      const title = (node.attrs.title as string) || '';
      const width = Math.round(node.attrs.width || 640);
      const height = Math.round(node.attrs.height || 360);
      const src = node.attrs.src as string;

      // Create markdown link: [title WxH](src)
      const linkText = `${title} ${width}x${height}`.trim();

      state.openNode('paragraph');
      state.openNode('link', undefined, { url: src });
      state.addNode('text', undefined, linkText);
      state.closeNode();
      state.closeNode();
    },
  },
}));

/**
 * Export all video plugins for registration
 */
export const videoPlugin: MilkdownPlugin[] = [remarkVideoPlugin, videoNode].flat();
